import { createComponentStore } from '../../utils/createComponentStore';

export const DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY = 'chatSessionId';

const generateUniqueKey = (prefix) =>
  prefix + '_' + Math.floor(Math.random() * 90000000 + 10000000);

const DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER = {
  key: DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY,
  type: 'text',
  desc: 'Chat session ID created or used by this run.',
  systemOutput: DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY,
  label: 'Chat Session ID',
};

const getDefaultOutputPlaceholders = () => [
  {
    ...DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER,
    key: generateUniqueKey('chatSessionId'),
  },
];

const isDefaultOutputPlaceholder = (placeholder) => {
  const systemOutput = String(placeholder?.systemOutput || '').trim();
  const key = String(placeholder?.key || '').trim();
  return (
    systemOutput === DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY ||
    key === DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY
  );
};

const ensureDefaultOutputPlaceholders = (placeholders) => {
  const normalized = Array.isArray(placeholders)
    ? placeholders.filter(Boolean).map((placeholder) => ({ ...placeholder }))
    : [];

  const systemPlaceholder =
    normalized.find(
      (placeholder) =>
        String(placeholder?.systemOutput || '').trim() ===
        DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY
    ) ||
    normalized.find(
      (placeholder) =>
        String(placeholder?.key || '').trim() ===
        DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY
    ) ||
    null;
  const userPlaceholders = normalized.filter((placeholder) => placeholder !== systemPlaceholder);
  const existingKey = String(systemPlaceholder?.key || '').trim();

  // Preserve an existing unique key, or generate a new one if it's the bare default or empty
  const resolvedKey =
    existingKey && existingKey !== DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY
      ? existingKey
      : generateUniqueKey('chatSessionId');

  return [
    {
      ...DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER,
      ...(systemPlaceholder || {}),
      key: resolvedKey,
      systemOutput: DEFAULT_CHAT_SESSION_OUTPUT_PLACEHOLDER_KEY,
    },
    ...userPlaceholders,
  ];
};

const getDefaultValidation = () => ({
  enabled: false,
  type: 'llm',
  completionCriteria: '',
  validationCode: '',
  validationModel: null,
  maxRetries: 3,
  onFail: 'retry',
});

const initData = () => ({
  agentId: null,
  message: '',
  imageContext: [],
  outputPlaceholders: getDefaultOutputPlaceholders(),
  sessionMode: 'new',
  chatSessionId: '',
  validation: getDefaultValidation(),
});

const onLoadData = (rawData) => {
  // v1 compat: if fields are nested under aiAgentData, flatten them
  const flat = rawData.aiAgentData
    ? { ...rawData, ...rawData.aiAgentData }
    : { ...rawData };
  delete flat.aiAgentData;

  const data = JSON.parse(JSON.stringify({ ...initData(), ...flat }));
  data.outputPlaceholders = ensureDefaultOutputPlaceholders(
    data.outputPlaceholders
  );
  data.validation = { ...getDefaultValidation(), ...(data.validation || {}) };
  return data;
};

const { getStore: getAIAgentStore, defaultStore } = createComponentStore({
  type: 'ai_agent',
  endpoint: 'ai_agent',
  registryKey: 'ai_agent',
  initData,
  onLoadData,

  extraActions: (set, get) => ({
    setAgentId: (value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data.agentId = value;
      set({ data });
    },

    setMessage: (value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data.message = value;
      set({ data });
    },

    setImageContext: (imageUrl) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (!data.imageContext) data.imageContext = [];
      if (data.imageContext.includes(imageUrl)) return;
      data.imageContext.push(imageUrl);
      set({ data });
    },

    deleteImageChip: (index) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (!data.imageContext) return;
      data.imageContext.splice(index, 1);
      set({ data });
    },

    setSessionMode: (value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data.sessionMode = value;
      set({ data });
    },

    setChatSessionId: (value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data.chatSessionId = value;
      set({ data });
    },

    setOutputPlaceholders: (value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      data.outputPlaceholders = ensureDefaultOutputPlaceholders(value);
      set({ data });
    },

    addOutputPlaceholder: () => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (!data.outputPlaceholders) data.outputPlaceholders = [];
      data.outputPlaceholders.push({ key: '', type: 'string', desc: '' });
      set({ data });
    },

    removeOutputPlaceholder: (index) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (data.outputPlaceholders) {
        const targetPlaceholder = data.outputPlaceholders[index];
        if (isDefaultOutputPlaceholder(targetPlaceholder)) return;
        data.outputPlaceholders.splice(index, 1);
        data.outputPlaceholders =
          ensureDefaultOutputPlaceholders(data.outputPlaceholders);
        set({ data });
      }
    },

    updateOutputPlaceholder: (index, field, value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (data.outputPlaceholders) {
        data.outputPlaceholders[index][field] = value;
        set({ data });
      }
    },

    setValidationField: (field, value) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (!data.validation) data.validation = getDefaultValidation();
      data.validation[field] = value;
      set({ data });
    },

    setValidationEnabled: (enabled) => {
      const data = JSON.parse(JSON.stringify(get().data));
      if (!data.validation) data.validation = getDefaultValidation();
      data.validation.enabled = enabled;
      set({ data });
    },
  }),
});

export { getAIAgentStore, isDefaultOutputPlaceholder };
export default defaultStore;
